import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import { FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { amenitiesList } from '../../../components/property/property-card/PropertyCardTypes';
import { detectDiscriminatoryContent } from '../../../utility/property-utilities/detectDiscriminatoryContent';
import { ListingFormData } from '../types';

interface DescriptionAmenitiesProps {
  register: UseFormRegister<ListingFormData>;
  errors: FieldErrors<ListingFormData>;
  watch: UseFormWatch<ListingFormData>;
}

const DescriptionAmenities: React.FC<DescriptionAmenitiesProps> = ({
  register,
  errors,
  watch
}) => {
  const title = watch('title');
  const description = watch('description');

  const detectedIssues = useMemo(() => {
    return detectDiscriminatoryContent(`${title || ''} ${description || ''}`);
  }, [title, description]);

  const descriptionLength = (description || '').length;

  return (
    <motion.div
      key="singleStep3"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Progress indicator */}
      <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg mb-6">
        <p className="text-sm text-indigo-800">
          <strong>Step 3 of 6:</strong> Make it shine!{' '}
          <strong>~60 seconds</strong> remaining
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <ClipboardIcon className="w-6 h-6 mr-2 text-blue-500" />
        Description & Amenities
      </h2>
      <div className="space-y-6">
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Tell Us About Your Property <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Describe what makes your property special. Include nearby amenities,
            transport links, and unique features.{' '}
            <strong>Detailed descriptions get 2x more inquiries!</strong>
          </p>
          <textarea
            {...register('description', {
              required: 'Description is required',
              minLength: {
                value: 50,
                message: 'Description must be at least 50 characters'
              }
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            }`}
            rows={6}
            placeholder="e.g., Spacious 2-bed apartment in Dublin City Centre. 5 mins walk to Luas, close to Trinity College. Modern kitchen with appliances, large living area, south-facing balcony. Secure bike storage and on-site laundry. Perfect for professionals or students..."
          />
          <div className="flex justify-between items-center mt-1">
            {errors.description ? (
              <span className="text-red-500 text-sm">
                {errors.description.message}
              </span>
            ) : (
              <span className="text-sm text-gray-500">
                {descriptionLength < 50 ? (
                  <span className="text-orange-600">
                    {50 - descriptionLength} more characters needed
                  </span>
                ) : (
                  <span className="text-green-600">✓ Great! Keep going</span>
                )}
              </span>
            )}
            <span className="text-sm text-gray-400">
              {descriptionLength} characters
            </span>
          </div>

          {detectedIssues.length > 0 && (
            <div className="bg-red-100 p-4 rounded text-red-600 mb-4 mt-4">
              <p className="font-bold">⚠️ Warning:</p>
              <p>
                This description may contain discriminatory content against our
                guidelines:
              </p>
              <ul className="list-disc list-inside mt-2">
                {detectedIssues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Amenities
          </label>
          <p className="text-sm text-gray-500 mb-3">
            Select all amenities that apply.{' '}
            <strong>Properties with 5+ amenities get viewed 40% more!</strong>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {amenitiesList.map((amenity: string) => (
              <label
                key={amenity}
                className="flex items-center text-gray-700 hover:bg-gray-50 p-2 rounded-lg cursor-pointer transition"
              >
                <input
                  type="checkbox"
                  value={amenity}
                  {...register('amenities')}
                  className="mr-3 h-5 w-5 text-blue-500 focus:ring-blue-500 rounded cursor-pointer"
                />
                {amenity}
              </label>
            ))}
          </div>
        </div>

        {/* Help text */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <p className="text-sm text-blue-800">
            <strong>💡 Pro tip:</strong> Mention specific locations (e.g., "10
            min walk to Luas") and highlight recent upgrades (e.g., "newly
            renovated kitchen")
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default DescriptionAmenities;
